/**
 * SMART FEEDBACK SYSTEM - Phase 3 Adaptive Intelligence
 * Detects specific error types and provides contextual feedback
 */

const SmartFeedback = {
    /**
     * Error type constants
     */
    ERROR_TYPES: {
        OPERATION_CONFUSION_SUM: 'operation_confusion_sum',  // Used sum instead of subtraction
        OPERATION_CONFUSION_SUB: 'operation_confusion_sub',  // Used subtraction instead of sum
        CARRYING_ERROR: 'carrying_error',                     // Forgot to carry in addition
        BORROWING_ERROR: 'borrowing_error',                   // Forgot to borrow in subtraction
        DIGIT_SWAP: 'digit_swap',                             // Swapped digits (32 vs 23)
        OFF_BY_ONE: 'off_by_one',                             // Result is ±1 from correct
        OFF_BY_TEN: 'off_by_ten',                             // Result is ±10 from correct
        MAGNITUDE_CONFUSION: 'magnitude_confusion',          // Wrong greater/lesser choice
        UNKNOWN: 'unknown'
    },

    /**
     * Analyze an operation error (Step 4)
     * @param {string} expectedOp - Expected operation ('suma' or 'resta')
     * @param {string} userSelection - User's selection
     * @returns {Object} Error analysis { type, message }
     */
    analyzeOperationError(expectedOp, userSelection) {
        if (userSelection === 'suma' && expectedOp === 'resta') {
            return {
                type: this.ERROR_TYPES.OPERATION_CONFUSION_SUM,
                message: '¿Seguro? Si quitas cosas, ¿debería salir un número mayor?',
                hint: 'Cuando el problema dice "quitar", "perder" o "dar", normalmente necesitas restar.'
            };
        }

        if (userSelection === 'resta' && expectedOp === 'suma') {
            return {
                type: this.ERROR_TYPES.OPERATION_CONFUSION_SUB,
                message: '¿Seguro? Si añades cosas, ¿debería salir un número menor?',
                hint: 'Cuando el problema dice "añadir", "ganar" o "comprar más", normalmente necesitas sumar.'
            };
        }

        return {
            type: this.ERROR_TYPES.UNKNOWN,
            message: 'Revisa tu respuesta con cuidado.',
            hint: null
        };
    },

    /**
     * Analyze a magnitude error (Step 4, substep 2)
     * @param {string} expectedMagnitude - Expected magnitude ('mayor' or 'menor')
     * @param {string} userSelection - User's selection
     * @param {string} operationType - Problem type ('suma' or 'resta')
     * @returns {Object} Error analysis { type, message }
     */
    analyzeMagnitudeError(expectedMagnitude, userSelection, operationType) {
        const isSuma = operationType === 'suma' || operationType === 'sum';

        if (isSuma && userSelection === 'menor') {
            return {
                type: this.ERROR_TYPES.MAGNITUDE_CONFUSION,
                message: 'Piensa: cuando sumas cosas, ¿terminas con más o con menos?',
                hint: 'Sumar es juntar, así que el resultado siempre es MAYOR.'
            };
        }

        if (!isSuma && userSelection === 'mayor') {
            return {
                type: this.ERROR_TYPES.MAGNITUDE_CONFUSION,
                message: 'Piensa: cuando quitas cosas, ¿terminas con más o con menos?',
                hint: 'Restar es quitar, así que el resultado siempre es MENOR.'
            };
        }

        return {
            type: this.ERROR_TYPES.UNKNOWN,
            message: 'Piensa en lo que pasa cuando sumas o restas.',
            hint: null
        };
    },

    /**
     * Analyze a calculation error (Step 5)
     * @param {number} num1 - First number
     * @param {number} num2 - Second number
     * @param {number} expectedResult - Correct result
     * @param {number} userResult - User's result
     * @param {string} operationType - 'suma' or 'resta'
     * @returns {Object} Error analysis { type, message, hint }
     */
    analyzeCalculationError(num1, num2, expectedResult, userResult, operationType) {
        const isSuma = operationType === 'suma' || operationType === 'sum';
        const diff = Math.abs(expectedResult - userResult);

        // Check for digit swap (e.g., 23 vs 32)
        if (this.isDigitSwap(expectedResult, userResult)) {
            return {
                type: this.ERROR_TYPES.DIGIT_SWAP,
                message: '¡Casi! Revisa el orden de los dígitos.',
                hint: 'Las decenas van a la izquierda y las unidades a la derecha.'
            };
        }

        // Check for off-by-one error
        if (diff === 1) {
            return {
                type: this.ERROR_TYPES.OFF_BY_ONE,
                message: '¡Muy cerca! Solo te has equivocado por 1.',
                hint: 'Cuenta con cuidado el último paso.'
            };
        }

        // Check for off-by-ten error (common carrying/borrowing mistake)
        if (diff === 10) {
            if (isSuma) {
                return {
                    type: this.ERROR_TYPES.CARRYING_ERROR,
                    message: '¡Casi! Parece que olvidaste la llevada.',
                    hint: 'Cuando la suma de las unidades pasa de 10, hay que llevarse 1 a las decenas.'
                };
            } else {
                return {
                    type: this.ERROR_TYPES.BORROWING_ERROR,
                    message: '¡Casi! Parece un problema con el préstamo.',
                    hint: 'Cuando no puedes restar la unidad, pide prestado 10 a las decenas.'
                };
            }
        }

        // Check for carrying error in addition
        if (isSuma && this.hasCarryingError(num1, num2, userResult)) {
            return {
                type: this.ERROR_TYPES.CARRYING_ERROR,
                message: '¡Casi! Revisa las llevadas.',
                hint: 'Cuando las unidades suman más de 9, no olvides llevar 1 a las decenas.'
            };
        }

        // Check for borrowing error in subtraction
        if (!isSuma && this.hasBorrowingError(num1, num2, userResult)) {
            return {
                type: this.ERROR_TYPES.BORROWING_ERROR,
                message: '¡Casi! Revisa el préstamo de las decenas.',
                hint: 'Cuando la unidad de arriba es menor que la de abajo, hay que pedir prestado.'
            };
        }

        // Generic feedback
        return {
            type: this.ERROR_TYPES.UNKNOWN,
            message: isSuma
                ? 'Revisa tu suma paso a paso.'
                : 'Revisa tu resta paso a paso.',
            hint: isSuma
                ? 'Suma primero las unidades, después las decenas.'
                : 'Resta primero las unidades, después las decenas.'
        };
    },

    /**
     * Check if two numbers are digit swaps of each other
     */
    isDigitSwap(expected, user) {
        const expStr = String(expected);
        const userStr = String(user);

        if (expStr.length !== userStr.length || expStr.length < 2) {
            return false;
        }

        return expStr.split('').reverse().join('') === userStr;
    },

    /**
     * Detect carrying error in addition
     */
    hasCarryingError(num1, num2, userResult) {
        const units1 = num1 % 10;
        const units2 = num2 % 10;
        const unitsSum = units1 + units2;

        // If units sum >= 10, there should be a carry
        if (unitsSum >= 10) {
            const correctResult = num1 + num2;
            const userUnits = userResult % 10;
            const correctUnits = correctResult % 10;

            // If user's units are correct but total is wrong by 10, it's a carry error
            if (userUnits === correctUnits && Math.abs(userResult - correctResult) === 10) {
                return true;
            }

            // If user got sum without carry
            if (userResult === (Math.floor(num1 / 10) + Math.floor(num2 / 10)) * 10 + (units1 + units2)) {
                return true;
            }
        }

        return false;
    },

    /**
     * Detect borrowing error in subtraction
     */
    hasBorrowingError(num1, num2, userResult) {
        const units1 = num1 % 10;
        const units2 = num2 % 10;

        // If units1 < units2, a borrow is needed
        if (units1 < units2) {
            const correctResult = num1 - num2;
            const diff = Math.abs(userResult - correctResult);

            // Common borrow error: diff of 10 or 20
            if (diff === 10 || diff === 20) {
                return true;
            }

            // User might have subtracted the smaller from larger without borrowing
            if (userResult === (Math.floor(num1 / 10) - Math.floor(num2 / 10)) * 10 + (units2 - units1)) {
                return true;
            }
        }

        return false;
    },

    /**
     * Get enhanced feedback message with optional hint
     * @param {Object} analysis - Analysis result from analyze* methods
     * @returns {string} Combined message
     */
    getEnhancedMessage(analysis) {
        if (analysis.hint) {
            return `${analysis.message}\n\n💡 ${analysis.hint}`;
        }
        return analysis.message;
    },

    /**
     * Track error for recommendation engine
     * @param {string} errorType - Type of error
     */
    trackError(errorType) {
        if (window.RecommendationEngine) {
            RecommendationEngine.recordError(errorType);
        }
    }
};

// Expose globally
window.SmartFeedback = SmartFeedback;

console.log('🧠 smartFeedback.js loaded');
